import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaLock, FaLockOpen, FaPlay, FaPause, FaCheck, FaStepForward, FaTimes, FaRedo, FaClock } from 'react-icons/fa';
import { API_URL } from '../config';

const CourseModuleList = ({ courseId, modules, candidateId, token, isEnrolled }) => {
  const navigate = useNavigate();
  const [moduleProgress, setModuleProgress] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(false);
  const [showResumeDialog, setShowResumeDialog] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const [expandedModules, setExpandedModules] = useState([]);

  useEffect(() => {
    if (isEnrolled && candidateId && token) {
      fetchProgressSummary();
    }
  }, [isEnrolled, candidateId, courseId, token]);

  const fetchProgressSummary = async () => {
    setLoadingProgress(true);
    try {
      console.log('CourseModuleList: Fetching progress for', courseId, 'candidate', candidateId);
      const response = await fetch(
        `${API_URL}/course-teaching/progress/summary/${courseId}?candidate_id=${candidateId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        console.log('CourseModuleList: Progress data received', data);
        setModuleProgress(data);
      } else {
        console.error('CourseModuleList: Failed to fetch progress', response.status);
      }
    } catch (error) {
      console.error('Error fetching progress:', error);
    } finally {
      setLoadingProgress(false);
    }
  };

  const handleModuleClick = (module, moduleIndex) => {
    if (!isEnrolled) {
      alert('Please enroll in the course first');
      return;
    }

    const progress = moduleProgress?.modules?.find(
      m => m.module_number === moduleIndex + 1
    );
    
    if (progress?.is_locked) {
      alert('Please complete previous modules to unlock this one');
      return;
    }
    
    if (progress?.status === 'paused') {
      setSelectedModule({ ...module, progress, moduleNumber: moduleIndex + 1 });
      setShowResumeDialog(true);
    } else {
      startModule(module, moduleIndex + 1);
    }
  };

  const startModule = async (module, moduleNumber) => {
    try {
      await fetch(`${API_URL}/course-teaching/progress/start`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          candidate_id: candidateId,
          course_id: courseId,
          module_id: module.id || `module_${moduleNumber}`,
          module_number: moduleNumber
        })
      });
      
      navigate(`/course/${courseId}/module/${module.id || `module_${moduleNumber}`}/teach`);
    } catch (error) {
      console.error('Error starting module:', error);
    }
  };

  const handleScrollToModule = () => {
    const firstUnlocked = moduleProgress?.modules?.find(m => !m.is_locked);
    
    if (firstUnlocked) {
      const moduleElement = document.getElementById(`module-${firstUnlocked.module_number}`);
      if (moduleElement) {
        moduleElement.scrollIntoView({ 
          behavior: 'smooth',
          block: 'center'
        });
        
        moduleElement.classList.add('highlight-module');
        setTimeout(() => {
          moduleElement.classList.remove('highlight-module');
        }, 2000);
      }
    }
  };

  const formatTime = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const toggleModule = (index) => {
    setExpandedModules(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const renderModule = (module, index) => {
    const progress = moduleProgress?.modules?.find(
      m => m.module_number === index + 1
    );
    
    const isLocked = progress ? progress.is_locked : (index !== 0);
    const status = progress?.status || 'not_started';
    const isExpanded = expandedModules.includes(index);
    
    // Generate sample lessons if not provided
    const lessons = module.lessons_details || Array.from({ length: module.lessons || 5 }, (_, i) => ({
      title: `Lesson ${i + 1}`,
      duration: '10 min'
    }));
    
    console.log(`Module ${index + 1}:`, { progress, isLocked, status, hasLessons: !!module.lessons_details });
    
    return (
      <div 
        key={index}
        id={`module-${index + 1}`}
        style={{
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          marginBottom: '16px',
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          opacity: isLocked ? 0.7 : 1,
          backgroundColor: 'white',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}
      >
        {/* Module Header */}
        <div 
          style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            padding: '20px',
            cursor: 'pointer',
            backgroundColor: status === 'completed' ? '#f0fdf4' : 
                            status === 'paused' ? '#fffbeb' : 
                            status === 'in_progress' ? '#eff6ff' : 'white',
            borderBottom: isExpanded ? '1px solid #e5e7eb' : 'none'
          }}
          onClick={() => !isLocked && toggleModule(index)}
        >
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Status Icon */}
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: isLocked ? '#f3f4f6' : 
                              status === 'completed' ? '#dcfce7' : 
                              status === 'in_progress' ? '#dbeafe' : '#f3f4f6',
              flexShrink: 0
            }}>
              {isLocked ? (
                <FaLock style={{ color: '#9ca3af', fontSize: '18px' }} />
              ) : status === 'completed' ? (
                <FaCheck style={{ color: '#16a34a', fontSize: '18px' }} />
              ) : status === 'paused' ? (
                <FaPause style={{ color: '#f59e0b', fontSize: '18px' }} />
              ) : (
                <FaPlay style={{ color: '#3b82f6', fontSize: '16px' }} />
              )}
            </div>
            
            {/* Module Info */}
            <div style={{ flex: 1 }}>
              <h4 style={{ 
                margin: '0 0 4px 0', 
                fontSize: '16px',
                fontWeight: '600',
                color: '#111827',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                {module.title}
                {!isLocked && (
                  <span style={{ 
                    fontSize: '14px', 
                    color: '#6b7280',
                    transition: 'transform 0.3s ease',
                    transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    display: 'inline-block'
                  }}>
                    ▶
                  </span>
                )}
              </h4>
              <p style={{ 
                margin: 0, 
                color: '#6b7280',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <FaClock style={{ fontSize: '12px' }} />
                  {module.duration}
                </span>
                <span>•</span>
                <span>{module.lessons} lessons</span>
                {status === 'completed' && (
                  <>
                    <span>•</span>
                    <span style={{ color: '#16a34a', fontWeight: '500' }}>Completed</span>
                  </>
                )}
              </p>
            </div>
          </div>
          
          {/* Action Button */}
          <div onClick={(e) => e.stopPropagation()}>
            {isLocked ? (
              <button 
                style={{
                  backgroundColor: '#f3f4f6',
                  color: '#9ca3af',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                disabled
              >
                <FaLock size={14} /> Locked
              </button>
            ) : status === 'completed' ? (
              <button 
                style={{
                  backgroundColor: '#16a34a',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'background-color 0.2s'
                }}
                onClick={() => handleModuleClick(module, index)}
                onMouseOver={(e) => e.target.style.backgroundColor = '#15803d'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#16a34a'}
              >
                <FaRedo size={14} /> Review
              </button>
            ) : status === 'paused' ? (
              <button 
                style={{
                  backgroundColor: '#f59e0b',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'background-color 0.2s'
                }}
                onClick={() => handleModuleClick(module, index)}
                onMouseOver={(e) => e.target.style.backgroundColor = '#d97706'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#f59e0b'}
              >
                <FaPlay size={14} /> Resume
              </button>
            ) : status === 'in_progress' ? (
              <button 
                style={{
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'background-color 0.2s'
                }}
                onClick={() => handleModuleClick(module, index)}
                onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
              >
                <FaPlay size={14} /> Continue
              </button>
            ) : (
              <button 
                style={{
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'background-color 0.2s'
                }}
                onClick={() => handleModuleClick(module, index)}
                onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
              >
                <FaPlay size={14} /> Start
              </button>
            )}
          </div>
        </div>
        
        {/* Expandable Lesson Details */}
        {isExpanded && (
          <div style={{
            padding: '20px',
            backgroundColor: '#f9fafb'
          }}>
            <div style={{ 
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <h5 style={{ 
                margin: 0, 
                color: '#374151', 
                fontSize: '14px', 
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Course Content
              </h5>
              <span style={{
                backgroundColor: '#e5e7eb',
                color: '#6b7280',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '500'
              }}>
                {lessons.length} lessons
              </span>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {lessons.map((lesson, lessonIndex) => (
                <div 
                  key={lessonIndex}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    transition: 'all 0.2s',
                    cursor: isLocked ? 'not-allowed' : 'default'
                  }}
                  onMouseOver={(e) => {
                    if (!isLocked) {
                      e.currentTarget.style.borderColor = '#3b82f6';
                      e.currentTarget.style.boxShadow = '0 1px 3px rgba(59, 130, 246, 0.1)';
                    }
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <span style={{ 
                    minWidth: '28px', 
                    height: '28px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    backgroundColor: '#eff6ff',
                    color: '#3b82f6',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '600'
                  }}>
                    {lessonIndex + 1}
                  </span>
                  <span style={{ 
                    flex: 1, 
                    color: '#111827',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    {lesson.title || `Lesson ${lessonIndex + 1}`}
                  </span>
                  {lesson.duration && (
                    <span style={{ 
                      color: '#6b7280', 
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <FaClock size={12} />
                      {lesson.duration}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Progress Bar */}
        {progress && progress.completion_percentage > 0 && (
          <div style={{
            padding: '0 20px 16px 20px'
          }}>
            <div style={{
              width: '100%',
              height: '6px',
              backgroundColor: '#e5e7eb',
              borderRadius: '3px',
              overflow: 'hidden'
            }}>
              <div 
                style={{
                  height: '100%',
                  backgroundColor: '#16a34a',
                  width: `${progress.completion_percentage}%`,
                  transition: 'width 0.3s ease',
                  borderRadius: '3px'
                }}
              />
            </div>
            <p style={{
              margin: '8px 0 0 0',
              fontSize: '12px',
              color: '#6b7280',
              textAlign: 'right'
            }}>
              {progress.completion_percentage}% complete
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {isEnrolled && moduleProgress && (
        <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
          <h3>Your Progress</h3>
          <p>Completed: {moduleProgress.completed_modules} / {moduleProgress.total_modules} modules</p>
          <button 
            onClick={handleScrollToModule}
            style={{
              backgroundColor: '#2196f3',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Continue Learning
          </button>
        </div>
      )}

      <div>
        {modules && modules.map((module, index) => renderModule(module, index))}
      </div>

      {showResumeDialog && selectedModule && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <FaClock style={{ fontSize: '32px', color: '#2196f3' }} />
              <h3 style={{ margin: 0 }}>Resume Learning</h3>
            </div>
            
            <div style={{ marginBottom: '24px' }}>
              <p>You paused this module at <strong>{formatTime(selectedModule.progress?.pause_timestamp)}</strong></p>
              <p>Do you want to continue from where you left off?</p>
            </div>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button 
                style={{
                  backgroundColor: '#9e9e9e',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onClick={() => {
                  setShowResumeDialog(false);
                  setSelectedModule(null);
                }}
              >
                <FaTimes /> Cancel
              </button>
              
              <button 
                style={{
                  backgroundColor: '#ff9800',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onClick={() => {
                  setShowResumeDialog(false);
                  startModule(selectedModule, selectedModule.moduleNumber);
                }}
              >
                <FaRedo /> Start Fresh
              </button>
              
              <button 
                style={{
                  backgroundColor: '#2196f3',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onClick={() => {
                  navigate(`/course/${courseId}/module/${selectedModule.progress.module_id}/teach?resume=true`);
                }}
              >
                <FaPlay /> Resume
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseModuleList;
